import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faIndustry,
  faCalendarAlt,
  faArrowRight,
  faLock,
  faRotateLeft,
  faDiagramProject,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import {
  SupplierRoutePath,
  buildSupplierDiagramPath,
  buildSupplierDraftReviewPath,
} from 'modules/FreeSupplier/routes';
import { draftTotalEf, formatDraftInterval } from 'modules/FreeSupplier/supplierDraftPresentation';
import { supplierUploadRejectionCopy } from 'modules/FreeSupplier/supplierUploadPresentation';
import { UploadQueueItemStatus } from 'modules/FreeSupplier/supplierUploadQueue';
import { useUpgradeModal } from './UpgradeModal';
import { useMockData } from '../state/MockDataContext';
import { installationNameById } from '../state/adapters';
import CommsTemplateDropzone from './CommsTemplateDropzone';
import TemplateIssuesPanel from 'modules/Common/templateIssues/TemplateIssuesPanel';

// B3: the dropzone-to-result state machine for the S6 upload → draft pipeline
// — shared by the general Template Upload page and the request-tailored
// upload page (S7.4). Owns its own lifecycle end to end (dropzone, parse
// wait, produced-drafts result, monitoring-plan upsell); the caller owns page
// chrome (heading, back link, request-context banner) so a route never
// renders a whole OTHER route page nested inside it.
//
// POST /supplier/uploads accepts exactly ONE file per request, so a
// multi-file drop is uploaded SEQUENTIALLY by CommsTemplateDropzone's own
// queue (supplierUploadQueue.js) — its onComplete fires once with every
// SETTLED queue item once the whole batch is done AND at least one draft
// was produced. NOTHING is published here: the terminal CTA is always
// "review the extracted values", never a completed/published state.
//
// - lockedInstallation (R1, S7 fix batch 3): forwarded verbatim to
//   CommsTemplateDropzone — see its own doc comment. Absent for the plain
//   `/supplier/upload` flow (TemplateUploadPage).
export default function SupplierTemplateUploadFlow({ lockedInstallation }) {
  const { installationDocs } = useMockData();
  const { openUpgrade } = useUpgradeModal();
  // The settled upload-queue items ({id, file, status, result, ...}) from
  // CommsTemplateDropzone, once the batch is done — cleared by "Upload another".
  const [queueItems, setQueueItems] = useState(null);

  const installationName = (id) => installationNameById(installationDocs, id);

  const successItems = (queueItems || []).filter((it) => it.status === UploadQueueItemStatus.Done);
  const failedItems = (queueItems || []).filter((it) => it.status === UploadQueueItemStatus.Failed);
  const drafts = successItems.flatMap((it) => it.result?.drafts || []);
  const filesWithWarnings = successItems.filter((it) => it.result?.warnings?.length > 0);
  const fileCount = successItems.length;
  const reviewPath =
    drafts.length === 1 ? buildSupplierDraftReviewPath(drafts[0].id) : SupplierRoutePath.Drafts;

  // Item A2: "detect from template" (S10 backend amendment) can create a
  // brand-new installation when no existing one matches — call that out in
  // the aggregate summary. Deduped by installation id: two files resolving
  // to the SAME newly-created installation count as one new installation,
  // not two.
  const createdInstallationCount = new Set(
    successItems
      .filter((it) => it.result?.upload?.resolvedInstallation?.resolution === 'created')
      .map((it) => it.result.upload.resolvedInstallation.id),
  ).size;

  if (!queueItems) {
    return (
      <CommsTemplateDropzone
        onComplete={setQueueItems}
        lockedInstallation={lockedInstallation}
      />
    );
  }

  return (
    // Item B1: matches the batch queue list's own width (CommsTemplateDropzone)
    // so the page doesn't visibly narrow the instant a batch settles.
    <div className="flex flex-col gap-6 w-full max-w-3xl mx-auto">
      {/* Complete: the produced draft(s) — review is required before anything publishes. */}
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 bg-primaryColor/5 border-b border-primaryColor/10">
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-primaryColor text-lg"
          />
          <div>
            <p className="text-sm font-semibold text-slate-800 m-0">
              {fileCount === 1
                ? `${successItems[0].result.upload.originalFilename} parsed successfully`
                : `${fileCount} templates parsed`}
            </p>
            <p className="text-xs text-slate-400 m-0">
              {drafts.length} draft{drafts.length === 1 ? '' : 's'} ready for review — nothing is
              shared yet
              {createdInstallationCount > 0
                ? ` · ${createdInstallationCount} new installation${
                    createdInstallationCount === 1 ? '' : 's'
                  } added`
                : ''}
              {failedItems.length > 0
                ? ` · ${failedItems.length} file${failedItems.length === 1 ? '' : 's'} couldn't be processed`
                : ''}
            </p>
          </div>
        </div>

        {failedItems.length > 0 && (
          <ul className="list-none m-0 p-0 px-6 py-3 bg-red-50/60 border-b border-red-100 flex flex-col gap-1.5">
            {failedItems.map((item) => {
              const copy = item.clientError
                ? { title: item.clientError }
                : supplierUploadRejectionCopy(item.serverError?.code, item.serverError?.message);
              return (
                <li
                  key={item.id}
                  className="text-xs text-red-700"
                >
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      className="text-red-400 text-[11px]"
                    />
                    <span className="font-medium truncate">{item.file.name}</span>
                    <span className="text-red-600/70">— {copy.title}</span>
                  </span>
                  {/* Which cells have to be corrected before this file can be sent again. */}
                  <TemplateIssuesPanel
                    parsedData={{ issues: item.serverError?.issues }}
                    outcome="refused"
                    className="mt-2"
                  />
                </li>
              );
            })}
          </ul>
        )}

        {/* What the parser assumed while reading the files it accepted — the
            drafts below were built on these values. */}
        {filesWithWarnings.map((item) => (
          <div
            key={item.id}
            className="px-6 py-3 border-b border-slate-100"
          >
            <p className="text-xs font-medium text-slate-600 m-0 mb-2 truncate">{item.file.name}</p>
            <TemplateIssuesPanel parsedData={{ issues: item.result.warnings }} />
          </div>
        ))}

        <ul className="list-none m-0 p-0 divide-y divide-slate-100">
          {drafts.map((draft) => {
            const total = draftTotalEf(draft);
            return (
              <li
                key={draft.id}
                className="px-6 py-4 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 m-0 flex items-center gap-2">
                    <FontAwesomeIcon
                      icon={faIndustry}
                      className="text-slate-300 text-xs"
                    />
                    {installationName(draft.targetSeriesKey.installationId)}
                  </p>
                  <p className="text-xs text-slate-500 m-0 mt-0.5 flex flex-wrap items-center gap-x-1">
                    <CgCnGoodHoverText
                      cnCode={draft.targetSeriesKey.cnCode}
                      useAutonomousData
                      variant="plain"
                    />
                    {draft.targetSeriesKey.productVariant ? (
                      <span>· {draft.targetSeriesKey.productVariant}</span>
                    ) : null}
                  </p>
                  <p className="text-[11px] text-slate-400 m-0 mt-0.5 flex items-center gap-1.5">
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      className="text-[10px]"
                    />
                    {formatDraftInterval(draft.interval)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-primaryColor m-0">
                    {total ?? '—'}
                    <span className="text-[10px] font-normal text-slate-400"> tCO2e/t</span>
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 m-0">
                    Proposed total
                  </p>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="px-6 py-4 border-t border-slate-100 flex flex-wrap items-center gap-3">
          <Link
            to={reviewPath}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-primaryColor hover:bg-primaryColor/90 rounded-xl px-4 py-2 no-underline transition-colors"
          >
            Review extracted values
            <FontAwesomeIcon
              icon={faArrowRight}
              className="text-xs"
            />
          </Link>
          {/* fileCount === 1 keeps the exact single-file label/behavior;
              a multi-file batch names each diagram by its source file so
              nothing produced by the batch is dropped from the summary. */}
          {successItems
            .filter((it) => it.result?.diagram)
            .map((it) => (
              <Link
                key={it.id}
                to={buildSupplierDiagramPath(it.result.diagram.id)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-xl px-3 py-2 no-underline hover:bg-slate-50 transition-colors"
              >
                <FontAwesomeIcon icon={faDiagramProject} />
                {fileCount === 1 ? 'View source diagram' : `View diagram — ${it.file.name}`}
              </Link>
            ))}
          <CgRadixButton
            variant="ghost"
            icon={faRotateLeft}
            onClick={() => setQueueItems(null)}
          >
            Upload another
          </CgRadixButton>
        </div>
      </div>

      {/* Monitoring plan upsell */}
      <button
        type="button"
        onClick={() => openUpgrade('compliance')}
        className="rounded-2xl border border-primaryColor/25 bg-gradient-to-r from-primaryColor/10 to-transparent p-4 flex items-center gap-4 text-left w-full hover:from-primaryColor/15 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-primaryColor/15 flex items-center justify-center shrink-0">
          <FontAwesomeIcon
            icon={faLock}
            className="text-primaryColor"
          />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-800 m-0">
            Generate your monitoring plan from this template
          </p>
          <p className="text-xs text-slate-500 m-0 mt-0.5">
            Premium turns your reviewed data into a CBAM monitoring plan — no re-typing.
          </p>
        </div>
        <FontAwesomeIcon
          icon={faArrowRight}
          className="text-primaryColor text-xs shrink-0"
        />
      </button>
    </div>
  );
}
